import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Role display names
const ROLE_DISPLAY_NAMES = {
  system_admin: "System Administrator",
  executive: "Executive",
  operations_manager: "Operations Manager",
  case_manager: "Case Manager",
  support_staff: "Support Staff",
  read_only_auditor: "Read-Only Auditor"
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [token, setToken] = useState(localStorage.getItem('myhelp_token'));
  const [loading, setLoading] = useState(true);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [autoLogoutReason, setAutoLogoutReason] = useState(null);
  const autoLogoutTimerRef = useRef(null);
  const autoLogoutConfigRef = useRef(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const [userResponse, permResponse] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/auth/permissions`)
      ]);
      setUser(userResponse.data);
      setPermissions(permResponse.data.permissions || []);
      setMustResetPassword(userResponse.data.must_reset_password || false);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  // Auto-logout functionality
  const checkAutoLogout = useCallback(() => {
    const config = autoLogoutConfigRef.current;
    if (!config || !config.enabled) return;
    
    try {
      // Get current time in user's timezone
      const now = new Date();
      const userTimezone = config.user_timezone || 'America/Chicago';
      
      // Format current time in user's timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const currentTime = formatter.format(now);
      
      // Check if current time matches logout time (within 1 minute window)
      const [logoutHour, logoutMin] = config.logout_time.split(':').map(Number);
      const [currentHour, currentMin] = currentTime.split(':').map(Number);
      
      if (currentHour === logoutHour && currentMin === logoutMin) {
        // Trigger automatic logout
        console.log('Auto-logout triggered at configured time:', config.logout_time);
        setAutoLogoutReason('Your session has ended in accordance with the organization\'s security policy.');
        logout();
      }
    } catch (error) {
      console.error('Error checking auto-logout:', error);
    }
  }, []);

  // Fetch auto-logout configuration and start timer
  useEffect(() => {
    if (!user || !token) {
      // Clear timer if logged out
      if (autoLogoutTimerRef.current) {
        clearInterval(autoLogoutTimerRef.current);
        autoLogoutTimerRef.current = null;
      }
      return;
    }

    const fetchAutoLogoutConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/auto-logout-config`);
        autoLogoutConfigRef.current = response.data;
        
        if (response.data.enabled) {
          // Check every minute
          autoLogoutTimerRef.current = setInterval(checkAutoLogout, 60000);
          // Also check immediately
          checkAutoLogout();
        }
      } catch (error) {
        console.error('Failed to fetch auto-logout config:', error);
      }
    };

    fetchAutoLogoutConfig();

    return () => {
      if (autoLogoutTimerRef.current) {
        clearInterval(autoLogoutTimerRef.current);
      }
    };
  }, [user, token, checkAutoLogout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, must_reset_password } = response.data;
    localStorage.setItem('myhelp_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setMustResetPassword(must_reset_password || userData.must_reset_password || false);
    
    // Fetch permissions after login
    try {
      const permResponse = await axios.get(`${API}/auth/permissions`);
      setPermissions(permResponse.data.permissions || []);
    } catch (e) {
      console.error('Failed to fetch permissions:', e);
    }
    
    return userData;
  };

  // "Sign in as": keep the admin's own token aside, use the target user's token until Exit
  const signInAs = async (userId) => {
    const res = await axios.post(`${API}/auth/impersonate`, { user_id: userId });
    localStorage.setItem('myhelp_admin_token', localStorage.getItem('myhelp_token'));
    localStorage.setItem('myhelp_token', res.data.access_token);
    window.location.assign('/dashboard');
  };

  const exitSignInAs = async () => {
    const adminToken = localStorage.getItem('myhelp_admin_token');
    try { await axios.post(`${API}/auth/impersonate/exit`); } catch (e) { /* still return to own account */ }
    localStorage.removeItem('myhelp_admin_token');
    if (adminToken) localStorage.setItem('myhelp_token', adminToken); else localStorage.removeItem('myhelp_token');
    window.location.assign('/dashboard');
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('myhelp_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('myhelp_token');
    localStorage.removeItem('myhelp_admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPermissions([]);
    setMustResetPassword(false);
    // Clear auto-logout timer
    if (autoLogoutTimerRef.current) {
      clearInterval(autoLogoutTimerRef.current);
      autoLogoutTimerRef.current = null;
    }
  };

  const clearAutoLogoutReason = () => {
    setAutoLogoutReason(null);
  };

  const clearPasswordReset = () => {
    setMustResetPassword(false);
    if (user) {
      setUser({ ...user, must_reset_password: false });
    }
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    // System admin and executive have all permissions
    if (user.role === 'system_admin' || user.role === 'executive') return true;
    return permissions.includes(permission);
  };

  // Check if user can access a module/navigation item
  const canAccess = (module) => {
    const modulePermissions = {
      dashboard: 'view_dashboard',
      participants: 'view_participants',
      enrollments: 'view_enrollments',
      cases: 'view_cases',
      case_notes: 'view_case_notes',
      documents: 'view_documents',
      communications: 'view_communications',
      reports: 'view_reports',
      contracts: 'view_contracts',
      admin: 'view_admin',
      settings: 'view_settings',
      audit_logs: 'view_audit_logs'
    };
    
    const permission = modulePermissions[module];
    if (!permission) return false;
    return hasPermission(permission);
  };

  // Check if user can perform a CRUD operation
  const canPerform = (action, entity) => {
    const permission = `${action}_${entity}`;
    return hasPermission(permission);
  };

  // Get role display name
  const getRoleDisplayName = (role) => {
    return ROLE_DISPLAY_NAMES[role] || role;
  };

  // Check if user is admin (system_admin or executive)
  const isAdmin = () => {
    return user && (user.role === 'system_admin' || user.role === 'executive');
  };

  // Check if user can edit (not read-only)
  const canEdit = () => {
    return user && user.role !== 'read_only_auditor';
  };

  const value = {
    user,
    token,
    loading,
    permissions,
    mustResetPassword,
    autoLogoutReason,
    login,
    register,
    logout,
    signInAs,
    exitSignInAs,
    refreshUser,
    clearPasswordReset,
    clearAutoLogoutReason,
    hasPermission,
    canAccess,
    canPerform,
    getRoleDisplayName,
    isAdmin,
    canEdit,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
